import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { THEME_MODE_STORAGE_KEY, ThemeMode } from './theme-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private systemMql?: MediaQueryList;
  private systemMqlListener?: (ev: MediaQueryListEvent) => void;

  readonly mode = signal<ThemeMode>(this.loadMode());
  readonly forcedTheme = signal<Exclude<ThemeMode, 'system'> | null>(null);

  constructor() {
    effect(() => {
      const mode = this.mode();
      const forced = this.forcedTheme();
      this.applyToDom(forced ?? mode);
    });

    // Persist ONLY the user's chosen mode (never the forced override).
    effect(() => {
      this.saveMode(this.mode());
    });
  }

  setMode(mode: ThemeMode) {
    this.mode.set(mode);
  }

  forceTheme(theme: Exclude<ThemeMode, 'system'> | null) {
    this.forcedTheme.set(theme);
  }

  private applyToDom(mode: ThemeMode) {
    const el = this.doc.documentElement;
    if (mode === 'system') {
      const view = this.doc.defaultView;
      const matchMedia = view?.matchMedia?.bind(view);

      // Fallback: preserve previous behavior if matchMedia is not available.
      if (!matchMedia) {
        el.removeAttribute('data-theme');
        return;
      }

      this.systemMql ??= matchMedia('(prefers-color-scheme: light)');

      const applyResolvedTheme = () => {
        el.setAttribute('data-theme', this.systemMql!.matches ? 'light' : 'dark');
      };

      applyResolvedTheme();

      if (!this.systemMqlListener) {
        this.systemMqlListener = () => {
          if (this.mode() !== 'system') return;
          applyResolvedTheme();
        };
        this.systemMql.addEventListener('change', this.systemMqlListener);
      }
      return;
    }

    if (this.systemMql && this.systemMqlListener) {
      this.systemMql.removeEventListener('change', this.systemMqlListener);
      this.systemMqlListener = undefined;
    }
    el.setAttribute('data-theme', mode);
  }

  private loadMode(): ThemeMode {
    try {
      const raw = this.doc.defaultView?.localStorage.getItem(THEME_MODE_STORAGE_KEY);
      if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
      return 'system';
    } catch {
      return 'system';
    }
  }

  private saveMode(mode: ThemeMode) {
    try {
      this.doc.defaultView?.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }
}
